import IconImage from '@/components/settings/IconImage'
// import { BalanceCard } from "@/components/settings/BalanceCard";
import { LogoutConfirmationBottomSheet } from '@/components/settings/bottom-sheets/LogoutConfirmation'
import SettingsMenuItem from '@/components/settings/SettingsMenuItem'
import { Palette, Ban } from 'lucide-react'
// import { UsersCard } from "@/components/settings/UsersCard";
import { ROUTES } from '@/constants/routes'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { signOut } from '@/auth/firebase'
import PageHeader from '@/components/settings/PageHeader'
import { CurrenciesBottomSheet } from '@/components/shared/bottom-sheets/CurrenciesBottomSheet'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useUserProfile } from '@/hooks/api/useUsers'
import { useUpdateCurrency } from '@/hooks/useUpdateCurrency'
import { useUserPreferences } from '@/stores/userPreferences'
import { DesktopSettingsRoute } from '@/components/settings/desktop/DesktopSettingsRoute'
import { SaveButtonColourBottomSheet } from '@/components/settings/bottom-sheets/SaveButtonColourBottomSheet'

const settingsMenuItems = [
    // {
    //   icon: <IconImage name="money" alt="Money" />,
    //   title: "Your subscription price",
    //   description: "Setting price on your subscriptions",
    //   path: "/settings/subscriptions/price",
    // },
    {
        icon: <IconImage name="account" alt="Account" />,
        title: 'Account',
        description: 'Name, description, website',
        path: ROUTES.ACCOUNT,
    },
    {
        icon: <IconImage name="money" alt="Currency" />,
        title: 'Currency',
        description: 'Select your preferred currency',
        path: null,
    },
    {
        icon: <IconImage name="settings" alt="Settings" />,
        title: 'Manage subscriptions',
        description: 'Plan, credits & billing',
        path: '/settings/subscriptions/manage',
    },
    {
        icon: <IconImage name="archive" alt="Archive" />,
        title: 'Archive',
        description: 'Your product history',
        path: 'archive',
    },
    {
        icon: <IconImage name="chat-centered" alt="Chat-centered" />,
        title: 'Feedback',
        description: 'Request new features, report bugs',
        path: ROUTES.FEEDBACK,
    },
    {
        icon: <IconImage name="chat-centered" alt="Contact" />,
        title: 'Contact & Support',
        description: 'Legal info & get help',
        path: ROUTES.CONTACT_SUPPORT,
    },
    {
        icon: <Palette size={24} className="text-gray-700" />,
        title: 'Save Button Colour',
        description: 'Customize your save button color',
        path: null,
    },
    {
        icon: <Ban size={24} className="text-gray-700" />,
        title: 'Blocked Users',
        description: 'Manage blocked accounts',
        path: '/settings/blocked-users',
    },
    {
        icon: <IconImage name="logout" alt="Logout" />,
        title: 'Logout',
        description: '',
        textColor: 'text-red-500',
    },
]

export const Route = createFileRoute('/settings/')({
    validateSearch: (search: Record<string, unknown>) => ({
        tab: typeof search.tab === 'string' ? search.tab : 'Account',
    }),
    component: RouteComponent,
})

function RouteComponent() {
    const search = Route.useSearch()
    const [isDesktop, setIsDesktop] = useState(
        typeof window !== 'undefined' && window.innerWidth >= 768
    )

    useEffect(() => {
        const handleResize = () => setIsDesktop(window.innerWidth >= 768)
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    if (isDesktop) {
        // Desktop: Open popup with tab from URL
        return <DesktopSettingsRoute initialTab={search.tab} />
    }

    // Mobile: render existing settings page
    return <SettingsPage />
}

const SettingsPage = () => {
    const navigate = useNavigate()
    const { user } = useCurrentUser()
    const { data: profile } = useUserProfile(user?.uid)
    const { preferredCurrency } = useUserPreferences()
    const { updateCurrency } = useUpdateCurrency()

    const [isLogoutConfirmationOpen, setIsLogoutConfirmationOpen] =
        useState(false)
    const [isCurrencyBottomSheetOpen, setIsCurrencyBottomSheetOpen] =
        useState(false)
    const [isColorBottomSheetOpen, setIsColorBottomSheetOpen] = useState(false)

    const handleItemClick = (item: (typeof settingsMenuItems)[0]) => {
        if (item.title === 'Logout') {
            setIsLogoutConfirmationOpen(true)
        } else if (item.title === 'Currency') {
            setIsCurrencyBottomSheetOpen(true)
        } else if (item.title === 'Save Button Colour') {
            setIsColorBottomSheetOpen(true)
        } else if (item.path) {
            navigate({ to: item.path })
        }
    }

    const handleLogoutConfirm = async () => {
        try {
            await signOut()
            navigate({ to: ROUTES.ONBOARDING })
        } catch (err) {
            console.error('Error signing out:', err)
        } finally {
            setIsLogoutConfirmationOpen(false)
        }
    }

    const handleCurrencyChange = async (currency: string) => {
        await updateCurrency(currency, {
            showSuccessToast: true,
            onSuccess: () => {
                setIsCurrencyBottomSheetOpen(false)
            },
        })
    }

    return (
        <>
            <div className="flex flex-col gap-4 overflow-y-auto bg-white px-2">
                <PageHeader title="Settings" />

                {/* <div className="flex gap-2 ">
          <BalanceCard onClick={() => navigate({ to: ROUTES.WALLET })} />
          <UsersCard />
        </div> */}

                <div className="flex flex-col gap-2 p-2">
                    {settingsMenuItems.map((item) => (
                        <SettingsMenuItem
                            key={item.title}
                            icon={item.icon}
                            title={item.title}
                            description={item.description}
                            onClick={() => handleItemClick(item)}
                            textColor={item.textColor}
                        />
                    ))}
                </div>
            </div>
            <LogoutConfirmationBottomSheet
                open={isLogoutConfirmationOpen}
                onClose={() => setIsLogoutConfirmationOpen(false)}
                onConfirm={handleLogoutConfirm}
            />
            <CurrenciesBottomSheet
                open={isCurrencyBottomSheetOpen}
                setOpen={setIsCurrencyBottomSheetOpen}
                currency={preferredCurrency || profile?.preferredCurrency || 'AUD'}
                setCurrency={handleCurrencyChange}
            />
            <SaveButtonColourBottomSheet
                open={isColorBottomSheetOpen}
                setOpen={setIsColorBottomSheetOpen}
            />
        </>
    )
}
